import { isEmpty } from 'lodash'

// The shared source vocabulary for both TCN config fields that pull values out of
// the call payloads: VOIP Campaign Name and Call Metadata. The backend resolves a
// token with one function (_resolve_campaign_token in tcn.py), so the UI keeps one
// definition of what a token can be.
//
// A token is either "<prefix>:<path>", where the path is a glom expression, or one
// of two whole-value tokens that are not paths at all.

export const PATH_PREFIXES = ['clientInfo', 'callData', 'userDetails', 'peerData']

// callDirection reads peer_data.callType; huntGroup reads
// call_data.linkbackHuntGroup and falls back to user_details.huntGroupName for
// MANUAL calls only. Neither is addressable as a path, so choosing one leaves
// nothing to type.
export const WHOLE_VALUE_TOKENS = ['callDirection', 'huntGroup']

export const isWholeValuePrefix = (prefix) => WHOLE_VALUE_TOKENS.includes(prefix)

// Grouped so the dropdown separates "the whole value" from "a path into a payload".
export const PREFIX_OPTION_GROUPS = [
  {
    label: 'Whole value',
    options: [
      { value: 'callDirection', label: 'Call Direction' },
      { value: 'huntGroup', label: 'Hunt Group' },
    ],
  },
  {
    label: 'Path into a payload',
    options: [
      { value: 'clientInfo', label: 'Client Info' },
      { value: 'callData', label: 'Call Data' },
      { value: 'userDetails', label: 'User Details' },
      { value: 'peerData', label: 'Peer Data' },
    ],
  },
]

const PREFIX_LABELS = PREFIX_OPTION_GROUPS.flatMap((group) => group.options).reduce(
  (acc, option) => ({ ...acc, [option.value]: option.label }),
  {}
)

export const labelForPrefix = (prefix) => PREFIX_LABELS[prefix] || prefix

// Per-prefix placeholder, so the path field teaches the shape of the payload it is
// addressing rather than showing one generic example.
export const PATH_EXAMPLES = {
  clientInfo: 'rows[fieldLabel=DBCLNT].fieldValue',
  callData: 'ivrData.someField',
  userDetails: 'skills[name=Ubiquity].description',
  peerData: 'callType',
}

/**
 * Split a stored token into the two controls that produced it.
 *
 * Partitions on the FIRST colon only: a predicate value is free text and may itself
 * contain one, e.g. "clientInfo:rows[fieldLabel=Note: something].fieldValue". A
 * whole-value token has no path.
 */
export const splitToken = (token) => {
  const trimmed = typeof token === 'string' ? token.trim() : ''
  if (isWholeValuePrefix(trimmed)) {
    return { prefix: trimmed, path: '' }
  }
  const colon = trimmed.indexOf(':')
  if (colon === -1) {
    return { prefix: '', path: '' }
  }
  return { prefix: trimmed.slice(0, colon), path: trimmed.slice(colon + 1) }
}

/** Inverse of splitToken. Must round-trip. */
export const joinToken = (prefix, path) => {
  if (!prefix) {
    return ''
  }
  if (isWholeValuePrefix(prefix)) {
    return prefix
  }
  return `${prefix}:${path || ''}`
}

/**
 * Whether a token is one the backend can resolve. A whole-value token stands alone;
 * anything else needs a known prefix AND a non-empty path, because the resolver
 * partitions on the colon and rejects an empty name.
 */
export const isQualifiedToken = (token) => {
  const { prefix, path } = splitToken(token)
  if (isWholeValuePrefix(prefix)) {
    return true
  }
  return PATH_PREFIXES.includes(prefix) && !isEmpty(path.trim())
}

// Metadata keys are constrained because Remote Desktop validates nothing and the
// consumers are unforgiving: processing-service substitutes "[field]" in checklist
// item names, so a bracket breaks templating, and its rules match keys
// case-sensitively, so spaces and mixed case silently never match. Kept in sync with
// TcnCallMetadataField in src/schemas/agent_connector_schema.py.
export const METADATA_KEY_PATTERN = /^[A-Za-z0-9_]+$/
